import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/api';
import { BookingStatus } from '@/lib/types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { 
  CalendarDays, 
  Search, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye, 
  CreditCard,
  Printer,
  Building,
  User,
  Trash2
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';

export default function AdminBookingsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Fetch all bookings
  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['admin/bookings'],
    enabled: !!user && (user.role === 'admin'),
  });
  
  // Cancel booking mutation
  const cancelBookingMutation = useMutation({
    mutationFn: async ({ bookingId, reason }: { bookingId: number; reason: string }) => {
      const res = await apiRequest('PATCH', `admin/bookings/${bookingId}/cancel`, { cancelReason: reason });
      // return res.json();
      return res;
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Booking cancelled successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['admin/bookings'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Failed to cancel booking: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  
  // Filter bookings based on search query and status filter
  const filteredBookings = bookings.filter((booking: any) => {
    const matchesSearch = 
      (booking.property?.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (booking.property?.area || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (booking.guestName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (booking.guestEmail || '').toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesStatusFilter = 
      statusFilter === 'all' || 
      booking.status.toLowerCase() === statusFilter.toLowerCase();
      
    return matchesSearch && matchesStatusFilter;
  });
  
  // Status badge component
  const getStatusBadge = (status: BookingStatus) => {
    switch (status.toLowerCase()) {
      case BookingStatus.CONFIRMED.toLowerCase():
        return <Badge className="bg-green-500 hover:bg-green-600">Confirmed</Badge>;
      case BookingStatus.PENDING.toLowerCase():
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">Pending</Badge>;
      case BookingStatus.CANCELLED.toLowerCase():
        return <Badge className="bg-red-500 hover:bg-red-600">Cancelled</Badge>;
      case BookingStatus.COMPLETED.toLowerCase():
        return <Badge className="bg-blue-500 hover:bg-blue-600">Completed</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };
  
  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
    }).format(price);
  };
  
  // Get counts for each status
  const statusCounts = {
    all: bookings.length,
    [BookingStatus.CONFIRMED.toLowerCase()]: bookings.filter((b: any) => b.status.toLowerCase() === BookingStatus.CONFIRMED.toLowerCase()).length,
    [BookingStatus.PENDING.toLowerCase()]: bookings.filter((b: any) => b.status.toLowerCase() === BookingStatus.PENDING.toLowerCase()).length,
    [BookingStatus.CANCELLED.toLowerCase()]: bookings.filter((b: any) => b.status.toLowerCase() === BookingStatus.CANCELLED.toLowerCase()).length,
    [BookingStatus.COMPLETED.toLowerCase()]: bookings.filter((b: any) => b.status.toLowerCase() === BookingStatus.COMPLETED.toLowerCase()).length,
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Bookings Management</h1>
        <p className="text-muted-foreground">Track and manage all property bookings and reservations</p>
      </div>
      
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search bookings by property, guest name or email..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="w-full sm:w-auto">
            <Select 
              value={statusFilter} 
              onValueChange={setStatusFilter}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bookings ({statusCounts.all})</SelectItem>
                <SelectItem value={BookingStatus.PENDING.toLowerCase()}>Pending ({statusCounts[BookingStatus.PENDING.toLowerCase()]})</SelectItem>
                <SelectItem value={BookingStatus.CONFIRMED.toLowerCase()}>Confirmed ({statusCounts[BookingStatus.CONFIRMED.toLowerCase()]})</SelectItem>
                <SelectItem value={BookingStatus.COMPLETED.toLowerCase()}>Completed ({statusCounts[BookingStatus.COMPLETED.toLowerCase()]})</SelectItem>
                <SelectItem value={BookingStatus.CANCELLED.toLowerCase()}>Cancelled ({statusCounts[BookingStatus.CANCELLED.toLowerCase()]})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Tabs defaultValue="all" onValueChange={setStatusFilter} value={statusFilter}>
          <TabsList className="grid grid-cols-5 mb-4">
            <TabsTrigger value="all" className="relative">
              All
              <span className="ml-1 text-xs">({statusCounts.all})</span>
            </TabsTrigger>
            <TabsTrigger value={BookingStatus.PENDING.toLowerCase()} className="relative">
              <Clock className="w-4 h-4 mr-1" />
              Pending
              <span className="ml-1 text-xs">({statusCounts[BookingStatus.PENDING.toLowerCase()]})</span>
            </TabsTrigger>
            <TabsTrigger value={BookingStatus.CONFIRMED.toLowerCase()} className="relative">
              <CheckCircle className="w-4 h-4 mr-1" />
              Confirmed
              <span className="ml-1 text-xs">({statusCounts[BookingStatus.CONFIRMED.toLowerCase()]})</span>
            </TabsTrigger>
            <TabsTrigger value={BookingStatus.COMPLETED.toLowerCase()} className="relative">
              <CreditCard className="w-4 h-4 mr-1" />
              Completed
              <span className="ml-1 text-xs">({statusCounts[BookingStatus.COMPLETED.toLowerCase()]})</span>
            </TabsTrigger>
            <TabsTrigger value={BookingStatus.CANCELLED.toLowerCase()} className="relative">
              <XCircle className="w-4 h-4 mr-1" />
              Cancelled
              <span className="ml-1 text-xs">({statusCounts[BookingStatus.CANCELLED.toLowerCase()]})</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" aria-label="Loading" />
        </div>
      ) : filteredBookings.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Booking Details</TableHead>
                <TableHead>Property</TableHead>
                <TableHead>Guest</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBookings.map((booking: any) => (
                <TableRow key={booking.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span className="font-medium">Booking #{booking.id}</span>
                      <span className="text-xs text-muted-foreground">
                        Created: {new Date(booking.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                        <Building className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{booking.property?.title || 'Unknown Property'}</div>
                        <div className="text-xs text-muted-foreground capitalize">{booking.property?.area || 'Unknown Area'}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{booking.guestName || 'Guest'}</div>
                        <div className="text-xs text-muted-foreground">{booking.guestEmail || 'No email'}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <div className="flex items-center text-sm">
                        <CalendarDays className="h-3 w-3 mr-1 text-muted-foreground" />
                        {booking.checkIn ? format(new Date(booking.checkIn), 'MMM d, yyyy') : 'Unknown'} 
                      </div>
                      <div className="flex items-center text-sm mt-1">
                        <CalendarDays className="h-3 w-3 mr-1 text-muted-foreground" />
                        {booking.checkOut ? format(new Date(booking.checkOut), 'MMM d, yyyy') : 'Unknown'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {booking.nightsCount || 'N/A'} nights, {booking.guestCount || 'N/A'} guests
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      {booking.totalAmount ? formatPrice(booking.totalAmount) : 'N/A'}
                    </div>
                    {booking.agentMarkupAmount && booking.agentMarkupAmount > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Includes {formatPrice(booking.agentMarkupAmount)} agent markup
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(booking.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {/* View Button */}
                      <Button 
                        variant="ghost"
                        size="icon"
                        title="View booking details"
                        onClick={() => window.location.href = `/admin/bookings/${booking.id}`}
                        className="bg-blue-50 hover:bg-blue-100"
                      >
                        <Eye className="h-4 w-4 text-blue-600" />
                      </Button>
                      
                      {/* Print Receipt Button */}
                      <Button 
                        variant="ghost"
                        size="icon"
                        title="Print booking receipt"
                        onClick={() => window.open(`bookings/${booking.id}/receipt`, '_blank')}
                        className="bg-gray-50 hover:bg-gray-100"
                      >
                        <Printer className="h-4 w-4 text-gray-600" />
                      </Button>
                      
                      {/* Cancel Button - only for pending or confirmed bookings */}
                      {(booking.status.toLowerCase() === BookingStatus.PENDING.toLowerCase() || 
                        booking.status.toLowerCase() === BookingStatus.CONFIRMED.toLowerCase()) && (
                        <Button 
                          variant="ghost"
                          size="icon"
                          title="Cancel booking"
                          onClick={() => {
                            if (confirm(`Are you sure you want to cancel this booking? This action cannot be undone.`)) {
                              const reason = prompt('Please provide a reason for cancellation:');
                              if (reason) {
                                cancelBookingMutation.mutate({
                                  bookingId: booking.id,
                                  reason
                                });
                              }
                            }
                          }}
                          className="bg-red-50 hover:bg-red-100"
                        >
                          <XCircle className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-16 bg-muted/30 rounded-lg">
          <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-bold mb-2">No bookings found</h3>
          <p className="text-muted-foreground mb-6">No bookings match your current search and filters</p>
          <Button variant="outline" onClick={() => {
            setSearchQuery('');
            setStatusFilter('all');
          }}>Clear Filters</Button>
        </div>
      )}
    </AdminLayout>
  );
}